/**
 * @voltxampere/engine — AC small-signal linearization at DC OP (Sprint 20)
 *
 * AC analysis runs in two phases:
 *
 *  1. The DC operating point converges (Newton-Raphson on the
 *     full nonlinear MNA system) — handled by the existing
 *     `DCSolver` in `dc/solver.ts`.
 *
 *  2. The circuit is linearised *around* that operating point and a
 *     complex MNA system is built per frequency point.
 *     Per-frequency the AC stamps re-derive the small-signal slopes
 *     (rd, gm, rπ, gds, …) from the frozen DC node voltages and
 *     branch currents.
 *
 * This module ships the lightweight envelope that the per-frequency
 * solver passes to AC stamps. The envelope is intentionally small:
 *
 *  - `dcNodeVoltages`: read-only Map of net id → DC voltage.
 *    Stamps subtract two entries to derive junction voltages
 *    (V_BE = V_B - V_E etc.) and pull the small-signal slope.
 *
 *  - `dcBranchCurrents`: read-only Map of component id → DC
 *    branch current. Augmented voltage sources (V, L) populate
 *    this; nonlinear devices that are not augmented compute the
 *    current from their own V-I curve.
 *
 *  - `temperature`: K — fed into Vt = kT/q for the diode/BJT
 *    small-signal slopes. Sprint 20 default 300.15 K (SPECS §3.5
 *    convention). `simulationSettings.ac.temperature` would be a
 *    natural Sprint 21+ extension; for now temperature is a
 *    constant.
 *
 * The AC matrix builder (Sprint 20 commit C2) consumes this object
 * once and shares it across every frequency in the sweep. Stamps
 * read from it but never mutate.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/** Default temperature when `simulationSettings.ac` does not pin one. */
export const DEFAULT_AC_TEMPERATURE_K = 300.15;
/**
 * Snapshot the DC OP and bind it together with the circuit and
 * temperature for downstream AC stamp dispatch. Pure data — no
 * solver work happens here.
 */
export function linearizeAtOP(circuit, dcResult, temperature = DEFAULT_AC_TEMPERATURE_K) {
    return {
        circuit,
        dcNodeVoltages: dcResult.nodeVoltages,
        dcBranchCurrents: dcResult.sourceCurrents,
        temperature,
    };
}
//# sourceMappingURL=linearize.js.map